/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useStoreLocatorList } from '@/extensions/store-locator/hooks/use-store-locator-list';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/typography';
import { Separator } from '@/components/ui/separator';
import StoreCard, { type LocatorStore } from './store-card';
import ListSkeleton from './list-skeleton';

/**
 * StoreLocatorList
 *
 * Presents search results and selection UI.
 *
 * @returns ReactElement
 */
export default function StoreLocatorList(): ReactElement | null {
    const { t } = useTranslation('extStoreLocator');
    const {
        config,
        selectedStoreInfo,
        setSelectedStoreInfo,
        geoError,
        hasSearched,
        hasError,
        isLoading,
        stores,
        storesPaginated,
        setPage,
    } = useStoreLocatorList();

    // Live NM shows no "viewing stores within N of X" sentence: the ZIP lives in the
    // search row above (see form.tsx), so the status line is deliberately absent.
    const statusMessage = null;

    const renderMessage = (text: string, variant: 'info' | 'error' = 'info') => (
        <div className="my-6 text-center" role="status">
            <Typography variant="large" as="div" className={variant === 'error' ? 'text-destructive' : ''}>
                {text}
            </Typography>
            <Separator className="mt-4" />
        </div>
    );

    // Show permission error immediately if present
    if (geoError) {
        return renderMessage(t('storeLocator.list.geoError'), 'error');
    }

    // Show fetch error if present
    if (hasError) {
        return renderMessage(t('storeLocator.list.fetchError'), 'error');
    }

    if (!hasSearched) {
        return null;
    }

    if (isLoading) {
        return <ListSkeleton statusMessage={statusMessage} />;
    }

    if (!stores.length) {
        return renderMessage(t('storeLocator.list.noResults'));
    }

    return (
        <div data-testid="store-locator-list">
            {storesPaginated.map((s, idx) => (
                <div key={s.id}>
                    {idx > 0 && <Separator />}
                    <StoreCard
                        store={s as LocatorStore}
                        distanceUnit={config.radiusUnit}
                        isSelected={selectedStoreInfo?.id === s.id}
                        onSelect={() => setSelectedStoreInfo(s)}
                    />
                </div>
            ))}
            {stores.length > storesPaginated.length && (
                <div className="mt-3">
                    <Button
                        variant="secondary"
                        className="w-full"
                        onClick={() => setPage((p) => p + 1)}
                        id="load-more-button">
                        {t('storeLocator.list.loadMoreButton')}
                    </Button>
                </div>
            )}
        </div>
    );
}
